/**
 * Billing and plan — Owner only.
 *
 * Visual Reference: `design/screens/settings-billing.html` — states default
 * (submissions past 80%, meter amber), **pastdue**, loading, error. Its loading
 * note is worth keeping: "Empty does not apply: a plan always exists, including
 * Free."
 *
 * THREE DELIBERATE DEVIATIONS FROM THE MOCKUP, each with a reason:
 *
 *   1. **The Voice wallet card does not render.** DECISION-016: "features dated
 *      to later milestones do not render at M1", and voice is M5. The mockup
 *      draws a balance, three top-up presets and an auto-top-up switch; every
 *      one of them would be a control that does nothing, and a `$0.00` balance
 *      "reads as broken rather than as not-yet-built" — which is the decision's
 *      own words about the cost tile it was written for.
 *
 *   2. **The plan card names the plan and the renewal date, not the price.**
 *      Leadgible stores no price: migration 0002 refused to seed one ("two
 *      sources of truth for money is the one duplication this table must not
 *      create") and migration 0012 held that line, mapping a plan to a provider
 *      VARIANT instead. The provider's subscription object reports no renewal
 *      amount either, so "$79 a month" would be a number invented on this
 *      screen. The invoices table below shows what was actually charged.
 *
 *   3. **The card's expiry line is absent.** The API carries a payment method
 *      as a SENTENCE — "Visa ending 4242" — because that is the only shape that
 *      leaves nowhere for card data to be assigned (Story 10.1). An expiry is a
 *      card field, and `tests/billing-provider-boundary.test.ts` would refuse
 *      it.
 *
 * THE THRESHOLDS ARE THE MOCKUP'S AND ARE ADOPTED: "a meter turns amber past
 * 80% and red past 95%, so you see a limit coming before it lands." They are
 * the same two numbers Story 10.5 notifies on, which is the point — the email
 * and the bar say the same thing at the same moment.
 *
 * Story 10.7.
 */
import { useCallback, useEffect, useState } from 'react'
import { apiGet, apiPost } from '../../lib/api'
import { useMe } from '../../shell/useMe'
import { Button } from '../../ui/Button'
import { Callout } from '../../ui/Callout'
import { Pill } from '../../ui/Pill'
import { Skeleton } from '../../ui/Skeleton'

type Meter = { used: number; limit: number }

type Invoice = {
  id: string
  createdAt: number
  totalCents: number
  currency: string
  status: string
  url: string | null
}

type BillingView = {
  plan: string
  status: string
  dunning: { state: string; entered_at: number | null }
  period: { start: number | null; end: number | null }
  cancel_at: number | null
  seats: Meter
  overage_available: boolean
  payment_method: string | null
  invoices: Invoice[]
  provider_unreachable: boolean
  usage: {
    period: string
    resets_at: number
    submissions: Meter
    analyses: Meter
    projected_limit_at: number | null
  }
}

/** `-1` is unlimited (`services/entitlements`), and has no bar to fill. */
const UNLIMITED = -1

const label = (plan: string) => plan.charAt(0).toUpperCase() + plan.slice(1)

const number = (value: number) => value.toLocaleString('en-GB')

const day = (at: number) =>
  new Date(at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })

const dayAndYear = (at: number) =>
  new Date(at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })

/** Integer cents to a displayed amount — DECISION-008, never a float anywhere. */
const money = (cents: number, currency: string) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(cents / 100)

/* ── the meter ──────────────────────────────────────────────────────────── */

/**
 * Amber past 80%, red past 95% — the mockup's thresholds, adopted.
 *
 * `--mdx-review` and `--mdx-error` rather than two new tokens: `Callout` above
 * records the same pairing and the same reasoning, and RULE-002 keeps red for
 * things that have actually gone wrong. A meter at 82% has not gone wrong.
 */
function tone(used: number, limit: number): 'ok' | 'warn' | 'over' {
  if (limit === UNLIMITED || limit <= 0) return 'ok'
  const fraction = used / limit
  if (fraction >= 0.95) return 'over'
  if (fraction >= 0.8) return 'warn'
  return 'ok'
}

const FILL = {
  ok: 'bg-mdx-brand',
  warn: 'bg-mdx-review',
  over: 'bg-mdx-error',
} as const

function MeterRow({
  name,
  meter,
  detail,
  suffix,
}: {
  name: string
  meter: Meter
  /** The explanatory line every meter carries in the mockup. */
  detail: string
  /** "this month" on submissions, nothing on the other two. */
  suffix?: string
}) {
  const unlimited = meter.limit === UNLIMITED
  const fraction = unlimited || meter.limit <= 0 ? 0 : Math.min(1, meter.used / meter.limit)
  const left = unlimited ? null : Math.max(0, meter.limit - meter.used)

  return (
    <div className="flex flex-col gap-mdx-8">
      <div className="flex flex-wrap items-baseline justify-between gap-mdx-8">
        <span className="text-mdx-15 font-mdx-semibold">{name}</span>
        <span className="text-mdx-15 text-mdx-slate">
          {unlimited
            ? `${number(meter.used)} used — no limit on this plan`
            : `${number(meter.used)} of ${number(meter.limit)}${suffix ? ` ${suffix}` : ''}`}
        </span>
      </div>
      <span
        aria-hidden="true"
        className="block h-mdx-10 w-full overflow-hidden rounded-mdx-pill bg-mdx-line"
      >
        <span
          className={`block h-full rounded-mdx-pill ${FILL[tone(meter.used, meter.limit)]}`}
          style={{ width: `${Math.round(fraction * 100)}%` }}
        />
      </span>
      <span className="text-mdx-13 text-mdx-slate">
        {left === null ? detail : `${number(left)} left. ${detail}`}
      </span>
    </div>
  )
}

/* ── the page ───────────────────────────────────────────────────────────── */

function Card({ children }: { children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-mdx-16 rounded-mdx-card border border-mdx-line bg-mdx-white p-mdx-24 shadow-mdx-card">
      {children}
    </section>
  )
}

export function Billing() {
  const me = useMe()
  const orgId = me.status === 'ready' ? (me.me.orgs[0]?.id ?? null) : null

  const [view, setView] = useState<BillingView | null>(null)
  const [loading, setLoading] = useState(true)
  const [opening, setOpening] = useState(false)

  const load = useCallback(async () => {
    if (!orgId) return
    setLoading(true)
    try {
      setView(await apiGet<BillingView>(`/v1/orgs/${orgId}/billing`))
    } catch {
      setView(null)
    } finally {
      setLoading(false)
    }
  }, [orgId])

  useEffect(() => {
    void load()
  }, [load])

  /**
   * Everything a customer can do to their subscription happens on the
   * provider's own page — PRD §4.6 chose a merchant of record precisely so
   * Leadgible never draws a card form. So "Change plan" and "Cancel" are one
   * button each into one hosted portal.
   */
  const openPortal = async () => {
    if (!orgId || opening) return
    setOpening(true)
    try {
      const { url } = await apiPost<{ url: string }>(`/v1/orgs/${orgId}/billing/portal`, {})
      window.location.assign(url)
    } catch {
      /**
       * A Free organisation has no portal to open — `openPortal` answers 409
       * with a message naming the fix (Story 10.1) — and a provider outage
       * answers 503. Neither is worth taking this page down for: the plan, the
       * meters and the dunning banner above are all read from D1 and are still
       * correct. Reloading re-reads them and shows the state again.
       */
      await load()
    } finally {
      setOpening(false)
    }
  }

  const header = (
    <header className="flex flex-col gap-mdx-8">
      <h1 className="m-0 text-mdx-h1 font-mdx-bold leading-mdx-tight tracking-mdx-h1">
        Billing and plan
      </h1>
      <p className="m-0 max-w-mdx-measure-52 text-mdx-15 text-mdx-slate">
        Your plan, what it has used, and what happens when a limit arrives.
      </p>
    </header>
  )

  /* ── loading ──────────────────────────────────────────────────────────── */
  if (loading) {
    return (
      <>
        {header}
        <div className="flex flex-col gap-mdx-20">
          {Array.from({ length: 3 }, (_, index) => (
            <Card key={index}>
              <Skeleton className="h-mdx-22 w-mdx-measure-34 max-w-full" />
              <Skeleton className="h-mdx-10 w-full rounded-mdx-pill" />
              <Skeleton className="h-mdx-10 w-full rounded-mdx-pill" />
            </Card>
          ))}
        </div>
      </>
    )
  }

  /* ── error ────────────────────────────────────────────────────────────── */
  if (!view) {
    return (
      <>
        {header}
        <section className="flex flex-col items-center gap-mdx-14 rounded-mdx-card border border-mdx-line bg-mdx-white px-mdx-28 py-mdx-48 text-center shadow-mdx-card">
          <span
            aria-hidden="true"
            className="flex size-mdx-40 shrink-0 items-center justify-center rounded-mdx-control bg-mdx-error text-mdx-20 font-mdx-bold text-mdx-white"
          >
            !
          </span>
          <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3">
            Couldn't load your billing details
          </span>
          <span className="max-w-mdx-measure-52 text-mdx-15 text-mdx-slate">
            Your plan is unchanged and nothing has been charged. This page just couldn't read it.
          </span>
          <Button onClick={() => void load()}>Try again</Button>
        </section>
      </>
    )
  }

  const { usage, dunning } = view
  const projected = usage.projected_limit_at

  return (
    <>
      {/**
       * PAST DUE IS AMBER, NOT RED, and the mockup says why: "access is
       * unchanged until 28 August and the copy names that date and exactly what
       * happens after it." Nothing has broken and nothing is lost — RULE-002's
       * discipline, the same one `Callout` records.
       */}
      {dunning.state !== 'none' && dunning.entered_at !== null ? (
        <Callout tone="notice">
          <strong className="font-mdx-semibold">
            {dunning.state === 'failed'
              ? "Your payment didn't go through."
              : dunning.state === 'readonly'
                ? 'This organisation is read-only until the payment is settled.'
                : 'This organisation is suspended until the payment is settled.'}
          </strong>{' '}
          {dunning.state === 'failed'
            ? `You have full access until ${day(dunning.entered_at + 7 * 86_400_000)}, then this workspace becomes read-only.`
            : 'Your funnels are still live and every lead is still being captured. Nothing has been deleted.'}
        </Callout>
      ) : null}

      {header}

      <div className="flex flex-col gap-mdx-20">
        {/* ── the plan ─────────────────────────────────────────────────── */}
        <Card>
          <div className="flex flex-wrap items-start justify-between gap-mdx-16">
            <div className="flex flex-col gap-mdx-4">
              <span className="text-mdx-12 font-mdx-bold uppercase tracking-mdx-eyebrow text-mdx-slate">
                Current plan
              </span>
              <span className="text-mdx-25 font-mdx-bold leading-mdx-heading tracking-mdx-h1">{label(view.plan)}</span>
              <span className="text-mdx-15 text-mdx-slate">
                {view.cancel_at !== null
                  ? `Ends ${dayAndYear(view.cancel_at)}`
                  : view.period.end !== null
                    ? `Renews ${dayAndYear(view.period.end)}`
                    : 'No renewal — this plan is free'}
              </span>
            </div>
            <div className="flex flex-wrap gap-mdx-10">
              <Button variant="secondary" onClick={() => void openPortal()} disabled={opening}>
                Change plan
              </Button>
              <Button variant="secondary" onClick={() => void openPortal()} disabled={opening}>
                Cancel
              </Button>
            </div>
          </div>
        </Card>

        {/* ── the meters ───────────────────────────────────────────────── */}
        <Card>
          <div className="flex flex-wrap items-baseline justify-between gap-mdx-8">
            <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3">This month</span>
            <span className="text-mdx-13 text-mdx-slate">Resets {day(usage.resets_at)}</span>
          </div>

          <MeterRow
            name="Submissions"
            meter={usage.submissions}
            suffix="this month"
            detail={
              projected === null
                ? 'Every lead is captured and scored either way — past the limit, the detail is held back until the plan changes.'
                : `At your current pace you'll reach the limit around ${day(projected)}.`
            }
          />
          <MeterRow
            name="AI analyses"
            meter={usage.analyses}
            detail="Scoring keeps working past the limit — only the written verdict pauses."
          />
          <MeterRow
            name="Seats"
            meter={view.seats}
            detail="Reviewers and viewers don't use a seat."
          />

          <p className="m-0 border-t border-mdx-line pt-mdx-16 text-mdx-13 text-mdx-slate">
            A meter turns amber past 80% and red past 95%, so you see a limit coming before it
            lands.
          </p>
        </Card>

        {/* ── payment method and invoices ──────────────────────────────── */}
        <Card>
          <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3">Payment</span>

          <div className="flex flex-wrap items-center justify-between gap-mdx-12">
            <span className="text-mdx-15">
              {view.payment_method ?? 'No payment method on file'}
            </span>
            <Button variant="secondary" onClick={() => void openPortal()} disabled={opening}>
              Update payment method
            </Button>
          </div>

          {view.provider_unreachable ? (
            /**
             * The page renders and says which part is missing. A billing screen
             * that failed outright during a provider outage would hide the
             * dunning banner above at exactly the moment it matters
             * (RULE-078, RULE-079).
             */
            <p className="m-0 text-mdx-13 text-mdx-slate">
              Your invoices and payment method couldn't be read just now. Your plan and its usage
              above are up to date, and nothing has been charged.
            </p>
          ) : view.invoices.length === 0 ? (
            <p className="m-0 text-mdx-13 text-mdx-slate">
              No invoices yet. They appear here after the first payment.
            </p>
          ) : (
            <ul className="m-0 flex list-none flex-col gap-mdx-2 p-0">
              {view.invoices.map((invoice) => (
                <li
                  key={invoice.id}
                  className="flex flex-wrap items-center gap-mdx-12 border-b border-mdx-line py-mdx-12 last:border-b-0"
                >
                  <span className="min-w-mdx-row-identity-sm grow text-mdx-15">
                    {dayAndYear(invoice.createdAt)}
                  </span>
                  <span className="text-mdx-15 font-mdx-semibold">
                    {money(invoice.totalCents, invoice.currency)}
                  </span>
                  {invoice.status === 'paid' ? null : (
                    <Pill tone="review">{label(invoice.status)}</Pill>
                  )}
                  {invoice.url ? (
                    <a
                      href={invoice.url}
                      className="shrink-0 text-right text-mdx-15 font-mdx-semibold text-mdx-brand-deep no-underline hover:underline"
                    >
                      Download
                    </a>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </>
  )
}
